export const FIXED_DETAIL_TAB_IDS = ['descripcion', 'imagenes'];
export const MAX_DETAIL_IMAGES = 4;

export const DETAIL_TABS = [
  { id: 'descripcion', type: 'description', nameKey: 'banners:v1.detail.tabs.descripcion' },
  { id: 'imagenes', type: 'images', nameKey: 'banners:v1.detail.tabs.imagenes' },
  { id: 'itinerario', type: 'itinerary', nameKey: 'banners:v1.detail.tabs.itinerario' },
  { id: 'incluye', type: 'editor', nameKey: 'banners:v1.detail.tabs.incluye' },
  { id: 'no-incluye', type: 'editor', nameKey: 'banners:v1.detail.tabs.noIncluye' },
  { id: 'hoteles', type: 'editor', nameKey: 'banners:v1.detail.tabs.hoteles' },
  { id: 'tarifas', type: 'editor', nameKey: 'banners:v1.detail.tabs.tarifas' },
  { id: 'salidas', type: 'editor', nameKey: 'banners:v1.detail.tabs.salidas' },
  { id: 'detalles', type: 'editor', nameKey: 'banners:v1.detail.tabs.detalles' },
];

export const DETAIL_TAB_BY_ID = DETAIL_TABS.reduce<Record<string, (typeof DETAIL_TABS)[number]>>(
  (acc, tab) => {
    acc[tab.id] = tab;
    return acc;
  },
  {}
);

export const DEFAULT_MOVABLE_TAB_ORDER = DETAIL_TABS.filter(
  (tab) => !FIXED_DETAIL_TAB_IDS.includes(tab.id)
).map((tab) => tab.id);

export function getOrderedDetailTabs(movableOrder?: string[]) {
  const fixed = FIXED_DETAIL_TAB_IDS.map((id) => DETAIL_TAB_BY_ID[id]).filter(Boolean);
  const order =
    Array.isArray(movableOrder) && movableOrder.length ? movableOrder : DEFAULT_MOVABLE_TAB_ORDER;
  const movable = order.map((id) => DETAIL_TAB_BY_ID[id]).filter(Boolean);
  return [...fixed, ...movable];
}

export function stripHtml(value: unknown) {
  if (!value) return '';
  if (typeof document === 'undefined') return String(value).replace(/<[^>]*>/g, '');

  const temp = document.createElement('div');
  temp.innerHTML = String(value);
  return temp.textContent || temp.innerText || '';
}

export function mapBannerImagenesToImages(bannerImagenes: unknown) {
  if (!Array.isArray(bannerImagenes)) return [];
  return bannerImagenes
    .filter(Boolean)
    .slice(0, MAX_DETAIL_IMAGES)
    .map((url, index) => ({
      name: String(url).split('/').pop() || `image-${index + 1}`,
      src: String(url),
    }));
}

export function mapItinerarioFromApi(
  items: Array<{ id: number; titulo?: string; descripcion?: string; orden?: number }> = []
) {
  return items
    .slice()
    .sort((a, b) => Number(a.orden) - Number(b.orden))
    .map((item) => ({
      id: `server-${item.id}`,
      serverId: item.id,
      title: item.titulo || '',
      description: item.descripcion || '',
      order: item.orden,
    }));
}

export function normalizeMovableOrder(ordenSecciones?: unknown) {
  if (!Array.isArray(ordenSecciones) || !ordenSecciones.length)
    return [...DEFAULT_MOVABLE_TAB_ORDER];
  const allowed = new Set(DEFAULT_MOVABLE_TAB_ORDER);
  const filtered = ordenSecciones.map(String).filter((id) => allowed.has(id));
  if (!filtered.length) return [...DEFAULT_MOVABLE_TAB_ORDER];
  const missing = DEFAULT_MOVABLE_TAB_ORDER.filter((id) => !filtered.includes(id));
  return [...filtered, ...missing];
}

export function defaultDetailData() {
  return {
    descripcion: '',
    itinerario: [] as Array<{ id: string; title: string; description: string; order?: number }>,
    incluye: '',
    'no-incluye': '',
    hoteles: '',
    tarifas: '',
    salidas: '',
    detalles: '',
  };
}

export function defaultDetailState() {
  return {
    movableOrder: [...DEFAULT_MOVABLE_TAB_ORDER],
    data: defaultDetailData(),
    images: [] as Array<{ name: string; src: string }>,
    saved: false,
  };
}

export function tabHasData(
  tab: { id: string } | undefined,
  detailState: {
    images?: Array<{ name: string; src: string }>;
    data: Record<string, unknown>;
  }
) {
  if (!tab || !detailState) return false;
  if (tab.id === 'imagenes') return (detailState.images || []).length > 0;
  if (tab.id === 'itinerario') {
    const items = detailState.data.itinerario as Array<{ title?: string; description?: string }>;
    return (items || []).some(
      (item) => (item.title || '').trim() || stripHtml(item.description).trim()
    );
  }
  return Boolean(stripHtml(detailState.data[tab.id] || '').trim());
}

export function buildDetailStateFromBanner(banner: Record<string, unknown> | null | undefined) {
  const base = defaultDetailState();
  if (!banner) return base;

  return {
    movableOrder: normalizeMovableOrder(banner.ordenSecciones),
    data: {
      ...base.data,
      descripcion: (banner.descripcionHTML as string) || '',
      incluye: (banner.incluye as string) || '',
      'no-incluye': (banner.noIncluye as string) || '',
      hoteles: (banner.hoteles as string) || '',
      tarifas: (banner.tarifas as string) || '',
      salidas: (banner.salida as string) || '',
      detalles: (banner.detalle as string) || '',
      itinerario: mapItinerarioFromApi(
        (banner.itinerarioBanners as Array<{
          id: number;
          titulo?: string;
          descripcion?: string;
          orden?: number;
        }>) || []
      ),
    },
    images: mapBannerImagenesToImages(banner.bannerImagenes),
    saved: true,
  };
}
